import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";
import { useToast } from "../contexts/ToastContext";
import CustomFieldsForm from "../components/ui/CustomFieldsForm";
import SalesInvoiceOcrTab from "./SalesInvoiceOcrTab";
import SalesImportModal from "../components/SalesImportModal";
import OpeningReceivablesModal from "../components/OpeningReceivablesModal";
import OpeningDepositsModal from "../components/OpeningDepositsModal";
import SalesCorrections from "../components/SalesCorrections";
import SalesReceivables from "../components/SalesReceivables";
import SalesInflowsToValidate from "../components/SalesInflowsToValidate";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: "ocr",         label: "À valider",    icon: "📄" },
  { id: "encaisser",   label: "À encaisser",  icon: "💶" }, // Q-clients (02/11/2026)
  { id: "devis",       label: "Devis",       icon: "📋" },
  { id: "commandes",   label: "Commandes",   icon: "📦" },
  { id: "bl",          label: "BL",          icon: "🚚" },
  { id: "factures",    label: "Factures",    icon: "🧾" },
  { id: "avoirs",      label: "Avoirs",      icon: "🔄" },
  { id: "relances",    label: "Relances",    icon: "📬" },
  { id: "echeancier",  label: "Échéancier",  icon: "📅" },
];

const TVA_RATES = [20, 10, 5.5, 2.1, 0];
const UNITS = ["pièce", "heure", "jour", "kg", "m²", "forfait", "m", "litre", "boîte", "lot"];

const STATUS_LABELS = {
  draft:     { label: "Brouillon",  color: "#4b5563" },
  pending:   { label: "En attente", color: "#f59e0b" },
  paid:      { label: "Payée",      color: "#16a34a" },
  late:      { label: "En retard",  color: "#ef4444" },
  cancelled: { label: "Annulée",    color: "#4b5563" },
};

const BL_STATUS_LABELS = {
  brouillon: { label: "Brouillon",  color: "#4b5563" },
  validé:    { label: "Validé",     color: "#3b82f6" },
  expédié:   { label: "Expédié",    color: "#f59e0b" },
  livré:     { label: "Livré",      color: "#16a34a" },
  annulé:    { label: "Annulé",     color: "#4b5563" },
};

function emptyLine() {
  return { product_ref: "", designation: "", description_longue: "", qty: 1, unit: "pièce", unit_price_ht: 0, remise_pct: 0, tva_rate: 20 };
}

function computeLineTotals(lines) {
  let ht = 0; let tva = 0;
  const tvaBuckets = {};
  for (const l of lines) {
    const lineHt = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price_ht) || 0) * (1 - (parseFloat(l.remise_pct) || 0) / 100);
    const rate   = parseFloat(l.tva_rate) || 20;
    ht += lineHt;
    if (!tvaBuckets[rate]) tvaBuckets[rate] = 0;
    tvaBuckets[rate] += lineHt * (rate / 100);
  }
  tva = Object.values(tvaBuckets).reduce((s, v) => s + v, 0);
  return { ht, tva, ttc: ht + tva, tvaBuckets };
}

function fmt(n) {
  return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ─────────────────────────────────────────────────────────────────
// Catalog picker (stock products)
// ─────────────────────────────────────────────────────────────────
function CatalogPicker({ companyId, onSelect, onClose }) {
  const [search, setSearch]     = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    api.stocks.products(companyId, { search, limit: 30 })
      .then(d => setProducts(d?.products || d || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [companyId, search]);

  return (
    <div style={{
      position: "absolute", top: 32, left: 0, zIndex: 999,
      background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
      width: 380, boxShadow: "0 8px 32px rgba(0,0,0,.4)",
    }}>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 8, alignItems: "center" }}>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un produit…"
          style={{ flex: 1, background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", color: "#111827", fontSize: 13 }}
        />
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
      </div>
      <div style={{ maxHeight: 280, overflowY: "auto" }}>
        {loading && <div style={{ padding: 20, color: "#4b5563", textAlign: "center" }}>Chargement…</div>}
        {!loading && products.length === 0 && <div style={{ padding: 20, color: "#4b5563", textAlign: "center" }}>Aucun produit trouvé</div>}
        {products.map(p => (
          <div key={p.id} onClick={() => onSelect(p)}
            style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid #1e293b", display: "flex", justifyContent: "space-between" }}
            onMouseEnter={e => e.currentTarget.style.background = "#e5e7eb"}
            onMouseLeave={e => e.currentTarget.style.background = ""}
          >
            <div>
              <div style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: "#4b5563" }}>{p.reference || "—"} · {p.unit || "pièce"}</div>
            </div>
            <div style={{ fontSize: 13, color: "#38bdf8", whiteSpace: "nowrap", marginLeft: 8 }}>
              {fmt(p.sale_price || p.unit_price || 0)} €
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// LineEditor — multi-line invoice item editor
// ─────────────────────────────────────────────────────────────────
function LineEditor({ lines, onChange, companyId }) {
  const [pickerIdx, setPickerIdx] = useState(null);

  function updateLine(i, field, value) {
    const next = lines.map((l, idx) => idx === i ? { ...l, [field]: value } : l);
    onChange(next);
  }
  function addLine() { onChange([...lines, emptyLine()]); }
  function removeLine(i) { onChange(lines.filter((_, idx) => idx !== i)); }

  function selectProduct(idx, product) {
    const next = lines.map((l, i) => i === idx ? {
      ...l,
      product_ref:   product.reference || "",
      designation:   product.name || "",
      unit:          product.unit || "pièce",
      unit_price_ht: parseFloat(product.sale_price || product.unit_price || 0),
    } : l);
    onChange(next);
    setPickerIdx(null);
  }

  const { ht, tva, ttc, tvaBuckets } = computeLineTotals(lines);

  const cell = { padding: "4px 3px", fontSize: 12, color: "#111827" };
  const inp = {
    width: "100%", background: "#ffffff", border: "1px solid #1e293b",
    borderRadius: 4, padding: "4px 6px", color: "#111827", fontSize: 12,
  };

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              {["", "Réf.", "Désignation *", "Qté", "Unité", "P.U. HT", "Remise %", "TVA %", "Total HT", ""].map((h, i) => (
                <th key={i} style={{ ...cell, textAlign: "left", fontWeight: 600, color: "#4b5563", padding: "6px 4px", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const lineHt = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price_ht) || 0) * (1 - (parseFloat(l.remise_pct) || 0) / 100);
              return (
                <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                  <td style={{ ...cell, width: 28, position: "relative" }}>
                    <button type="button" onClick={() => setPickerIdx(pickerIdx === i ? null : i)}
                      title="Sélectionner depuis le catalogue"
                      style={{ background: "none", border: "none", color: "#38bdf8", cursor: "pointer", fontSize: 14, padding: 2 }}>
                      📦
                    </button>
                    {pickerIdx === i && (
                      <CatalogPicker companyId={companyId}
                        onSelect={p => selectProduct(i, p)}
                        onClose={() => setPickerIdx(null)} />
                    )}
                  </td>
                  <td style={{ ...cell, width: 80 }}>
                    <input style={inp} value={l.product_ref} onChange={e => updateLine(i, "product_ref", e.target.value)} placeholder="REF-001" />
                  </td>
                  <td style={{ ...cell }}>
                    <input style={inp} value={l.designation} onChange={e => updateLine(i, "designation", e.target.value)} placeholder="Désignation *" required />
                    <input style={{ ...inp, marginTop: 2, fontSize: 11, color: "#4b5563" }}
                      value={l.description_longue || ""}
                      onChange={e => updateLine(i, "description_longue", e.target.value)}
                      placeholder="Description optionnelle…" />
                  </td>
                  <td style={{ ...cell, width: 60 }}>
                    <input style={inp} type="number" min="0" step="0.01" value={l.qty}
                      onChange={e => updateLine(i, "qty", e.target.value)} />
                  </td>
                  <td style={{ ...cell, width: 80 }}>
                    <select style={inp} value={l.unit} onChange={e => updateLine(i, "unit", e.target.value)}>
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </td>
                  <td style={{ ...cell, width: 90 }}>
                    <input style={inp} type="number" min="0" step="0.01" value={l.unit_price_ht}
                      onChange={e => updateLine(i, "unit_price_ht", e.target.value)} />
                  </td>
                  <td style={{ ...cell, width: 70 }}>
                    <input style={inp} type="number" min="0" max="100" step="0.1" value={l.remise_pct}
                      onChange={e => updateLine(i, "remise_pct", e.target.value)} />
                  </td>
                  <td style={{ ...cell, width: 70 }}>
                    <select style={inp} value={l.tva_rate} onChange={e => updateLine(i, "tva_rate", e.target.value)}>
                      {TVA_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </td>
                  <td style={{ ...cell, width: 90, textAlign: "right", fontWeight: 600 }}>
                    {fmt(lineHt)} €
                  </td>
                  <td style={{ ...cell, width: 28 }}>
                    <button type="button" onClick={() => removeLine(i)}
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16, padding: 2 }}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <button type="button" onClick={addLine}
          style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 13, cursor: "pointer" }}>
          + Ajouter une ligne
        </button>
        <div style={{ background: "#ffffff", border: "1px solid #1e293b", borderRadius: 8, padding: "10px 18px", minWidth: 220 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: "#4b5563" }}>Sous-total HT</span>
            <span>{fmt(ht)} €</span>
          </div>
          {Object.entries(tvaBuckets).map(([rate, amt]) => (
            <div key={rate} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2, color: "#4b5563" }}>
              <span>TVA {rate}%</span><span>{fmt(amt)} €</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid #1e293b", paddingTop: 6, marginTop: 6, display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 15 }}>
            <span>Total TTC</span><span style={{ color: "#38bdf8" }}>{fmt(ttc)} €</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PaymentScheduleEditor — éditeur d'échéancier de paiement
// ─────────────────────────────────────────────────────────────────
const SCHEDULE_TEMPLATES = {
  "30/70":    [{ label: "Acompte 30%", pct: 30 }, { label: "Solde 70%", pct: 70 }],
  "30/40/30": [{ label: "Acompte 30%", pct: 30 }, { label: "Intermédiaire 40%", pct: 40 }, { label: "Solde 30%", pct: 30 }],
  "50/50":    [{ label: "Acompte 50%", pct: 50 }, { label: "Solde 50%", pct: 50 }],
};

function PaymentScheduleEditor({ schedule, onChange, ttc }) {
  const inp = {
    width: "100%", background: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: 5, padding: "5px 8px", color: "#111827", fontSize: 12,
  };

  function applyTemplate(name) {
    const tpl = SCHEDULE_TEMPLATES[name];
    if (!tpl) return;
    onChange(tpl.map(t => ({
      label: t.label, percentage: t.pct,
      amount: Math.round(ttc * t.pct) / 100,
      due_date: "", status: "en_attente",
    })));
  }

  function updateRow(i, field, val) {
    onChange(schedule.map((r, idx) => {
      if (idx !== i) return r;
      const u = { ...r, [field]: val };
      if (field === "percentage") u.amount    = ttc > 0 ? Math.round(ttc * (parseFloat(val) || 0)) / 100 : 0;
      if (field === "amount")     u.percentage = ttc > 0 ? Math.round((parseFloat(val) || 0) / ttc * 10000) / 100 : 0;
      return u;
    }));
  }

  const totalPct = schedule.reduce((s, r) => s + (parseFloat(r.percentage) || 0), 0);
  const valid    = Math.abs(totalPct - 100) < 0.1;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#4b5563" }}>Modèle :</span>
        {Object.keys(SCHEDULE_TEMPLATES).map(t => (
          <button key={t} type="button" onClick={() => applyTemplate(t)}
            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 5, background: "#f9fafb", border: "1px solid #e5e7eb", color: "#4b5563", cursor: "pointer" }}>
            {t}
          </button>
        ))}
        <button type="button"
          onClick={() => onChange([...schedule, { label: `Échéance ${schedule.length + 1}`, percentage: 0, amount: 0, due_date: "", status: "en_attente" }])}
          style={{ marginLeft: "auto", fontSize: 11, padding: "3px 10px", borderRadius: 5, background: "#1e40af", border: "none", color: "#fff", cursor: "pointer" }}>
          + Ajouter
        </button>
      </div>

      {schedule.length === 0 ? (
        <div style={{ textAlign: "center", padding: "14px 0", color: "#4b5563", fontSize: 12 }}>
          Sélectionnez un modèle ou ajoutez des échéances manuellement.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
          <thead>
            <tr style={{ background: "#ffffff" }}>
              {["Label", "% du total", "Montant TTC (€)", "Date d'échéance", ""].map(h => (
                <th key={h} style={{ padding: "6px 6px", textAlign: "left", fontSize: 11, color: "#4b5563", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schedule.map((row, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                <td style={{ padding: "4px 4px" }}>
                  <input style={inp} value={row.label || ""} onChange={e => updateRow(i, "label", e.target.value)} />
                </td>
                <td style={{ padding: "4px 4px", width: 90 }}>
                  <input style={inp} type="number" min="0" max="100" step="0.01" value={row.percentage ?? 0}
                    onChange={e => updateRow(i, "percentage", e.target.value)} />
                </td>
                <td style={{ padding: "4px 4px", width: 120 }}>
                  <input style={inp} type="number" step="0.01" min="0" value={row.amount ?? 0}
                    onChange={e => updateRow(i, "amount", e.target.value)} />
                </td>
                <td style={{ padding: "4px 4px", width: 145 }}>
                  <input style={inp} type="date" value={row.due_date || ""}
                    onChange={e => updateRow(i, "due_date", e.target.value)} />
                </td>
                <td style={{ padding: "4px 4px", width: 28 }}>
                  <button type="button" onClick={() => onChange(schedule.filter((_, j) => j !== i))}
                    style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {schedule.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#4b5563" }}>Total :</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: valid ? "#16a34a" : "#ef4444" }}>
            {totalPct.toFixed(1)}% {valid ? "✓" : "— doit être 100%"}
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// InvoiceHTMLPreview — aperçu temps réel HTML (approxime le PDF)
// ─────────────────────────────────────────────────────────────────
function InvoiceHTMLPreview({ form, lines, schedule, paymentMode, company, invoiceId }) {
  const { showToast } = useToast();
  const { ht, tvaBuckets, ttc } = computeLineTotals(lines);
  const COLORS = { facture: '#6366F1', devis: '#F59E0B', avoir: '#EF4444', commande: '#3B82F6' };
  const BGS    = { facture: '#EEF2FF', devis: '#FFFBEB', avoir: '#FEF2F2', commande: '#EFF6FF' };
  const TITLES = { facture: 'FACTURE', devis: 'DEVIS', avoir: 'AVOIR', commande: 'BON DE COMMANDE' };
  const dc  = COLORS[form.doc_type] || '#6366F1';
  const dbg = BGS[form.doc_type]    || '#EEF2FF';
  const dt  = TITLES[form.doc_type] || 'FACTURE';
  const fA  = n => (n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const fD  = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR') : '—';
  const validLines = lines.filter(l => l.designation?.trim());

  return (
    <div style={{ background: '#fff', fontFamily: 'system-ui, sans-serif', fontSize: 12, color: '#1A1D2E', height: '100%' }}>
      {/* PDF link for saved invoices */}
      {invoiceId && (
        <div style={{ background: '#f8f9fc', padding: '7px 14px', borderBottom: '1px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={async () => {
              try {
                const docType = form.doc_type === 'avoir' ? 'avoir' : form.doc_type === 'devis' ? 'devis' : 'facture';
                const blob = await api.documents.invoicePdf(invoiceId, 'fr', docType);
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener,noreferrer');
              } catch (err) {
                showToast(err.message || 'Impossible de charger le PDF.', 'error');
              }
            }}
            style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, color: dc, textDecoration: 'none', fontWeight: 600, cursor: 'pointer' }}>
            📄 Ouvrir le PDF réel ↗
          </button>
        </div>
      )}

      {/* HEADER */}
      <div style={{ background: dc, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{company?.name || '—'}</div>
          <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 10, marginTop: 2 }}>
            {[company?.address, company?.city].filter(Boolean).join(', ')}
          </div>
          {company?.siret && <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 9.5, marginTop: 1 }}>SIRET {company.siret}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: 1 }}>{dt}</div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, marginTop: 3 }}>Date : {fD(form.date)}</div>
          {form.due_date && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>Échéance : {fD(form.due_date)}</div>}
        </div>
      </div>

      {/* ADDRESSES */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '10px 12px' }}>
        {[
          { label: 'ÉMETTEUR', name: company?.name, addr: company?.address, city: company?.city, email: null },
          { label: 'FACTURER À', name: form.client_name, addr: form.client_address, city: null, email: form.client_email },
        ].map(({ label, name, addr, city, email }) => (
          <div key={label} style={{ background: '#F8F9FC', padding: '7px 9px', borderLeft: `3px solid ${dc}`, borderRadius: 2 }}>
            <div style={{ color: dc, fontWeight: 700, fontSize: 7.5, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: 10.5 }}>{name || '—'}</div>
            {addr  && <div style={{ color: '#374151', fontSize: 9.5, marginTop: 1 }}>{addr}</div>}
            {city  && <div style={{ color: '#374151', fontSize: 9.5 }}>{city}</div>}
            {email && <div style={{ color: dc, fontSize: 9.5, marginTop: 1 }}>{email}</div>}
          </div>
        ))}
      </div>

      {/* ITEMS TABLE */}
      <div style={{ padding: '0 12px 8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: '#F8F9FC',
                      borderTop: `1.5px solid ${dc}`, borderBottom: `1px solid ${dc}`,
                      padding: '4px 6px', gap: 6, fontSize: 7.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase' }}>
          <span>Désignation</span>
          <span style={{ textAlign: 'right' }}>Qté × PU HT</span>
          <span style={{ textAlign: 'right' }}>Remise</span>
          <span style={{ textAlign: 'right' }}>Total HT</span>
        </div>
        {validLines.length === 0 && (
          <div style={{ textAlign: 'center', padding: 14, color: '#9CA3AF', fontSize: 10 }}>Ajoutez des lignes…</div>
        )}
        {validLines.map((l, i) => {
          const lHt = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price_ht) || 0) * (1 - (parseFloat(l.remise_pct) || 0) / 100);
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                  padding: '4px 6px', gap: 6, fontSize: 9.5,
                                  background: i % 2 === 0 ? '#fff' : '#FAFBFF', borderBottom: '1px solid #E5E7EB' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{l.designation}</div>
                {l.description_longue && <div style={{ color: '#6B7280', fontSize: 8.5, fontStyle: 'italic' }}>{l.description_longue}</div>}
              </div>
              <div style={{ textAlign: 'right', color: '#374151' }}>{l.qty} × {fA(l.unit_price_ht)}</div>
              <div style={{ textAlign: 'right', color: parseFloat(l.remise_pct) > 0 ? '#EF4444' : '#9CA3AF' }}>
                {parseFloat(l.remise_pct) > 0 ? `-${l.remise_pct}%` : '—'}
              </div>
              <div style={{ textAlign: 'right', fontWeight: 700 }}>{fA(lHt)}</div>
            </div>
          );
        })}
      </div>

      {/* TOTALS */}
      <div style={{ padding: '0 12px 12px', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ minWidth: 190 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', background: '#F8F9FC', fontSize: 9.5 }}>
            <span style={{ color: '#6B7280' }}>Total HT</span>
            <span style={{ fontWeight: 700 }}>{fA(ht)}</span>
          </div>
          {Object.entries(tvaBuckets).map(([rate, tvaAmt]) => (
            <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', background: '#F8F9FC', fontSize: 9.5 }}>
              <span style={{ color: '#6B7280' }}>TVA {rate} %</span>
              <span style={{ fontWeight: 700 }}>{fA(tvaAmt)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 8px', background: dbg, fontSize: 10, borderTop: '1px solid #e5e7eb' }}>
            <span style={{ color: dc, fontWeight: 700 }}>TOTAL TTC</span>
            <span style={{ color: dc, fontWeight: 700 }}>{fA(ttc)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 8px', background: dc, fontSize: 11 }}>
            <span style={{ color: '#fff', fontWeight: 700 }}>NET À PAYER</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{fA(ttc)}</span>
          </div>
        </div>
      </div>

      {/* ECHÉANCIER */}
      {paymentMode === 'echelonne' && schedule.length > 0 && (
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ color: dc, fontWeight: 700, fontSize: 8, textTransform: 'uppercase', marginBottom: 5 }}>Échéancier</div>
          {schedule.map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '3px 6px',
                                  background: i % 2 === 0 ? '#fff' : '#FAFBFF', borderBottom: '1px solid #E5E7EB', fontSize: 9.5 }}>
              <span>{s.label || `Échéance ${i + 1}`}</span>
              <span style={{ textAlign: 'right' }}>{fA(s.amount)}</span>
              <span style={{ textAlign: 'right', color: '#6B7280' }}>{s.due_date || '—'}</span>
            </div>
          ))}
        </div>
      )}

      {!invoiceId && (
        <div style={{ padding: '7px 12px', background: '#fffbeb', borderTop: '1px solid #fde68a', fontSize: 9.5, color: '#92400e' }}>
          💡 Enregistrez pour générer le PDF final
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// InvoiceModal — create/edit facture, devis, avoir
// ─────────────────────────────────────────────────────────────────
function InvoiceModal({ invoice, docType, companyId, onClose, onSaved }) {
  const { showToast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const due   = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    client_name:    invoice?.client_name    || "",
    client_email:   invoice?.client_email   || "",
    client_address: invoice?.client_address || "",
    date:           invoice?.date           || today,
    due_date:       invoice?.due_date       || due,
    description:    invoice?.description    || "",
    notes:          invoice?.notes          || "",
    language:       invoice?.language       || "fr",
    doc_type:       invoice?.doc_type       || docType || "facture",
    discount_rate:  invoice?.discount_rate  || "",
    discount_days:  invoice?.discount_days  || "",
  });
  const [lines, setLines]         = useState(invoice?.lines?.length ? invoice.lines : [emptyLine()]);
  const [saving, setSaving]       = useState(false);
  const savingRef                 = useRef(false);
  const [customers, setCustomers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [cfValues, setCfValues]   = useState({});
  const [paymentMode, setPaymentMode] = useState("single");
  const [schedule, setSchedule]   = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewCompany, setPreviewCompany] = useState(null);

  // entity_type pour champs perso : facture | devis | bon_commande
  const entityTypeForDoc = form.doc_type === "devis" ? "devis"
    : form.doc_type === "commande" ? "bon_commande" : "facture";

  useEffect(() => {
    if (companyId) api.customers.list(companyId).then(r => setCustomers(r?.data || r || [])).catch(() => {});
  }, [companyId]);

  // Charge les champs perso selon le type de document
  useEffect(() => {
    if (!companyId) return;
    api.customFields.list({ company_id: companyId, entity_type: entityTypeForDoc })
      .then(setCustomFields).catch(() => setCustomFields([]));
  }, [companyId, entityTypeForDoc]);

  // Charge les valeurs existantes si édition
  useEffect(() => {
    if (!invoice?.id || !companyId) return;
    api.customFields.getValues(entityTypeForDoc, invoice.id)
      .then(rows => {
        const map = {};
        rows.forEach(r => { map[r.field_id] = r.value; });
        setCfValues(map);
      }).catch(() => {});
  }, [invoice?.id, companyId, entityTypeForDoc]);

  // Charge les infos société pour l'aperçu
  useEffect(() => {
    if (!companyId) return;
    api.companies.get(companyId).then(setPreviewCompany).catch(() => {});
  }, [companyId]);

  // Charge l'échéancier existant si édition
  useEffect(() => {
    if (!invoice?.id) return;
    api.paymentSchedule.get(invoice.id)
      .then(rows => {
        if (rows.length > 0) {
          setPaymentMode("echelonne");
          setSchedule(rows);
        }
      }).catch(() => {});
  }, [invoice?.id]);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  async function save(e) {
    e.preventDefault();
    if (savingRef.current) return;
    if (lines.some(l => !l.designation?.trim())) {
      showToast("Chaque ligne doit avoir une désignation.", "error"); return;
    }
    // Validation champs perso obligatoires
    const missingRequired = customFields.filter(f => f.is_required && !cfValues[f.id]);
    if (missingRequired.length) {
      showToast(`Champ requis : ${missingRequired[0].field_label}`, "error"); return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = { ...form, company_id: companyId, lines };
      if (!form.due_date) delete payload.due_date;
      let savedId;
      if (invoice?.id) {
        await api.invoices.update(invoice.id, payload);
        savedId = invoice.id;
      } else {
        const created = await api.invoices.create(payload);
        savedId = created.id;
      }
      // Sauvegarde des champs personnalisés (non bloquant : la facture est deja enregistree a ce stade)
      if (savedId && customFields.length > 0) {
        try {
          const values = Object.entries(cfValues)
            .map(([field_id, value]) => ({ field_id: Number(field_id), value }));
          if (values.length > 0) {
            await api.customFields.saveValues({ entity_type: entityTypeForDoc, entity_id: savedId, values });
          }
        } catch (cfErr) {
          showToast("Facture enregistree, mais erreur sur les champs personnalises : " + cfErr.message, "error");
        }
      }
      // Sauvegarde de l'echeancier (non bloquant)
      if (paymentMode === "echelonne" && savedId && schedule.length > 0) {
        try {
          await api.paymentSchedule.set(savedId, schedule);
        } catch (schedErr) {
          showToast("Facture enregistree, mais erreur sur l'echeancier : " + schedErr.message, "error");
        }
      }
      const label = form.doc_type === "devis" ? "Devis" : form.doc_type === "avoir" ? "Avoir" : "Facture";
      showToast(`${label} ${invoice ? "modifié(e)" : "créé(e)"} !`, "success");
      onSaved();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const inp = {
    width: "100%", background: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: 6, padding: "8px 10px", color: "#111827", fontSize: 13,
  };
  const lbl = { fontSize: 12, color: "#4b5563", display: "block", marginBottom: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 16px", overflowY: "auto" }}>
      <form onSubmit={save} style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, width: "100%", maxWidth: 960, padding: 28, marginBottom: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: "#111827", fontSize: 18 }}>
            {invoice ? "Modifier" : "Créer"} {form.doc_type === "devis" ? "un devis" : form.doc_type === "avoir" ? "un avoir" : "une facture"}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button type="button" onClick={() => setShowPreview(p => !p)}
              style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, border: "1px solid",
                borderColor: showPreview ? "#6366f1" : "#e5e7eb",
                background: showPreview ? "#4f46e5" : "transparent",
                color: "#111827", cursor: "pointer" }}>
              {showPreview ? "Masquer l'aperçu" : "Aperçu PDF"}
            </button>
            <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 22, cursor: "pointer" }}>×</button>
          </div>
        </div>

        {!invoice && (
          <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
            {["facture", "devis"].map(t => (
              <button key={t} type="button" onClick={() => set("doc_type", t)}
                style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid",
                  borderColor: form.doc_type === t ? "#3b82f6" : "#e5e7eb",
                  background: form.doc_type === t ? "#1e40af" : "transparent",
                  color: form.doc_type === t ? "#ffffff" : "#111827", cursor: "pointer", fontSize: 13, textTransform: "capitalize" }}>
                {t}
              </button>
            ))}
          </div>
        )}

        <div style={{ background: "#ffffff", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: "#111827", fontSize: 13, marginBottom: 10 }}>Client</div>
          {customers.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <select onChange={e => { const c = customers.find(x => String(x.id) === e.target.value); if (c) { set("client_id", c.id); set("client_name", c.name); set("client_email", c.email || ""); set("client_address", c.address || ""); } }}
                style={inp}>
                <option value="">— Sélectionner un client du répertoire —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 12 }}>
            <div><label style={lbl}>Nom *</label><input style={inp} value={form.client_name} onChange={e => set("client_name", e.target.value)} required /></div>
            <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.client_email} onChange={e => set("client_email", e.target.value)} /></div>
            <div><label style={lbl}>Adresse</label><input style={inp} value={form.client_address} onChange={e => set("client_address", e.target.value)} /></div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div><label style={lbl}>Date *</label><input style={inp} type="date" value={form.date} onChange={e => set("date", e.target.value)} required /></div>
          <div><label style={lbl}>Échéance</label><input style={inp} type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} /></div>
          <div>
            <label style={lbl}>Langue</label>
            <select style={inp} value={form.language} onChange={e => set("language", e.target.value)}>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          <div><label style={lbl}>Escompte (%)</label><input style={inp} type="number" min="0" step="0.1" placeholder="2" value={form.discount_rate} onChange={e => set("discount_rate", e.target.value)} /></div>
        </div>

        <div style={{ background: "#ffffff", borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: "#111827", fontSize: 13, marginBottom: 10 }}>Lignes de détail</div>
          <LineEditor lines={lines} onChange={setLines} companyId={companyId} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div><label style={lbl}>Description (en-tête)</label><textarea style={{ ...inp, height: 64, resize: "vertical" }} value={form.description} onChange={e => set("description", e.target.value)} /></div>
          <div><label style={lbl}>Notes (pied de page)</label><textarea style={{ ...inp, height: 64, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
        </div>

        {customFields.length > 0 && (
          <div style={{ background: "#ffffff", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <CustomFieldsForm
              fields={customFields}
              values={cfValues}
              onChange={(fieldId, value) => setCfValues(p => ({ ...p, [fieldId]: value }))}
            />
          </div>
        )}

        {form.doc_type === "facture" && (
          <div style={{ background: "#ffffff", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>Conditions de paiement</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[["single", "Paiement unique"], ["echelonne", "Paiement échelonné"]].map(([mode, lbl2]) => (
                  <button key={mode} type="button"
                    onClick={() => { setPaymentMode(mode); if (mode === "single") setSchedule([]); }}
                    style={{ padding: "5px 14px", borderRadius: 6, fontSize: 12, border: "1px solid",
                      borderColor: paymentMode === mode ? "#3b82f6" : "#e5e7eb",
                      background: paymentMode === mode ? "#1e40af" : "transparent",
                      color: paymentMode === mode ? "#ffffff" : "#111827", cursor: "pointer" }}>
                    {lbl2}
                  </button>
                ))}
              </div>
            </div>
            {paymentMode === "echelonne" && (
              <PaymentScheduleEditor
                schedule={schedule}
                onChange={setSchedule}
                ttc={computeLineTotals(lines).ttc}
              />
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{ padding: "9px 20px", borderRadius: 7, background: "transparent", border: "1px solid #e5e7eb", color: "#4b5563", cursor: "pointer" }}>
            Annuler
          </button>
          <button type="submit" disabled={saving}
            style={{ padding: "9px 24px", borderRadius: 7, background: "#1e40af", border: "none", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>

      {/* Aperçu PDF — tiroir latéral droit */}
      {showPreview && (
        <div style={{
          position: "fixed", right: 0, top: 0, bottom: 0, width: 430,
          background: "#fff", zIndex: 110,
          boxShadow: "-6px 0 32px rgba(0,0,0,.4)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          <div style={{ padding: "10px 14px", background: "#1e1e2e", borderBottom: "1px solid #e5e7eb",
                        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#111827", fontWeight: 600, fontSize: 13 }}>Aperçu PDF (temps réel)</span>
            <button type="button" onClick={() => setShowPreview(false)}
              style={{ background: "none", border: "none", color: "#4b5563", fontSize: 18, cursor: "pointer" }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <InvoiceHTMLPreview
              form={form}
              lines={lines}
              schedule={schedule}
              paymentMode={paymentMode}
              company={previewCompany}
              invoiceId={invoice?.id}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BLModal — create/edit delivery note
// ─────────────────────────────────────────────────────────────────
function BLModal({ dn, companyId, onClose, onSaved }) {
  const { showToast } = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    client_name:      dn?.client_name      || "",
    client_email:     dn?.client_email     || "",
    client_address:   dn?.client_address   || "",
    delivery_date:    dn?.delivery_date    || today,
    delivery_address: dn?.delivery_address || "",
    carrier:          dn?.carrier          || "",
    tracking_number:  dn?.tracking_number  || "",
    notes:            dn?.notes            || "",
  });
  const [lines, setLines]       = useState(dn?.lines?.length ? dn.lines : [{ product_name: "", quantity_ordered: 1, quantity_delivered: 1, unit: "pièce", product_ref: "" }]);
  const [saving, setSaving]     = useState(false);
  const savingRef               = useRef(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (companyId) api.customers.list(companyId).then(r => setCustomers(r?.data || r || [])).catch(() => {});
  }, [companyId]);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }
  function updLine(i, f, v) { setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [f]: v } : l)); }
  function addLine() { setLines(ls => [...ls, { product_name: "", quantity_ordered: 1, quantity_delivered: 1, unit: "pièce", product_ref: "" }]); }
  function remLine(i) { setLines(ls => ls.filter((_, idx) => idx !== i)); }

  async function save(e) {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = { ...form, company_id: companyId, lines };
      if (dn?.id) await api.deliveryNotes.update(dn.id, payload);
      else await api.deliveryNotes.create(payload);
      showToast(`BL ${dn ? "modifié" : "créé"} !`, "success");
      onSaved();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  const inp = { width: "100%", background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 10px", color: "#111827", fontSize: 13 };
  const lbl = { fontSize: 12, color: "#4b5563", display: "block", marginBottom: 4 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 16px", overflowY: "auto" }}>
      <form onSubmit={save} style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, width: "100%", maxWidth: 900, padding: 28, marginBottom: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: "#111827", fontSize: 18 }}>{dn ? "Modifier" : "Créer"} un bon de livraison</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div style={{ background: "#ffffff", borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, color: "#111827", fontSize: 13, marginBottom: 8 }}>Destinataire</div>
          {customers.length > 0 && (
            <select onChange={e => { const c = customers.find(x => String(x.id) === e.target.value); if (c) { set("client_id", c.id); set("client_name", c.name); set("client_email", c.email || ""); set("client_address", c.address || ""); } }}
              style={{ ...inp, marginBottom: 10 }}>
              <option value="">— Sélectionner un client —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10 }}>
            <div><label style={lbl}>Nom *</label><input style={inp} value={form.client_name} onChange={e => set("client_name", e.target.value)} required /></div>
            <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.client_email} onChange={e => set("client_email", e.target.value)} /></div>
            <div><label style={lbl}>Adresse client</label><input style={inp} value={form.client_address} onChange={e => set("client_address", e.target.value)} /></div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div><label style={lbl}>Date livraison</label><input style={inp} type="date" value={form.delivery_date} onChange={e => set("delivery_date", e.target.value)} /></div>
          <div><label style={lbl}>Transporteur</label><input style={inp} value={form.carrier} onChange={e => set("carrier", e.target.value)} /></div>
          <div><label style={lbl}>N° suivi</label><input style={inp} value={form.tracking_number} onChange={e => set("tracking_number", e.target.value)} /></div>
          <div><label style={lbl}>Adresse livraison</label><input style={inp} value={form.delivery_address} onChange={e => set("delivery_address", e.target.value)} /></div>
        </div>

        <div style={{ background: "#ffffff", borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, color: "#111827", fontSize: 13, marginBottom: 8 }}>Articles</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#ffffff" }}>
                {["Référence", "Désignation *", "Qté commandée", "Qté livrée", "Unité", "N° lot", ""].map((h, i) => (
                  <th key={i} style={{ fontSize: 11, color: "#4b5563", padding: "5px 6px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                  <td style={{ padding: "4px 3px" }}><input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} value={l.product_ref || ""} onChange={e => updLine(i, "product_ref", e.target.value)} /></td>
                  <td style={{ padding: "4px 3px" }}><input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} value={l.product_name || ""} onChange={e => updLine(i, "product_name", e.target.value)} required /></td>
                  <td style={{ padding: "4px 3px", width: 110 }}><input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} type="number" min="0" step="0.01" value={l.quantity_ordered} onChange={e => updLine(i, "quantity_ordered", e.target.value)} /></td>
                  <td style={{ padding: "4px 3px", width: 100 }}><input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} type="number" min="0" step="0.01" value={l.quantity_delivered} onChange={e => updLine(i, "quantity_delivered", e.target.value)} /></td>
                  <td style={{ padding: "4px 3px", width: 90 }}>
                    <select style={{ ...inp, fontSize: 12, padding: "4px 6px" }} value={l.unit} onChange={e => updLine(i, "unit", e.target.value)}>
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "4px 3px", width: 90 }}><input style={{ ...inp, fontSize: 12, padding: "4px 6px" }} value={l.lot_number || ""} onChange={e => updLine(i, "lot_number", e.target.value)} /></td>
                  <td style={{ padding: "4px 3px", width: 28 }}>
                    <button type="button" onClick={() => remLine(i)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={addLine} style={{ marginTop: 8, background: "#1e40af", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>+ Ajouter un article</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Notes</label>
          <textarea style={{ ...inp, height: 56, resize: "vertical" }} value={form.notes} onChange={e => set("notes", e.target.value)} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onClose} style={{ padding: "9px 20px", borderRadius: 7, background: "transparent", border: "1px solid #e5e7eb", color: "#4b5563", cursor: "pointer" }}>Annuler</button>
          <button type="submit" disabled={saving} style={{ padding: "9px 24px", borderRadius: 7, background: "#1e40af", border: "none", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// InvoiceList — shared list for factures, devis, avoirs
// ─────────────────────────────────────────────────────────────────
const actionBtn = {
  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 5,
  color: "#111827", padding: "3px 8px", cursor: "pointer", fontSize: 11,
};

function InvoiceList({ docType, companyId, onNavigate }) {
  const { showToast } = useToast();
  const [data, setData]       = useState({ invoices: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all");
  const [search, setSearch]   = useState("");
  const [modal, setModal]     = useState(null);
  const [payDateModal, setPayDateModal] = useState(null); // { invoice, date }

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    api.invoices.list({ company_id: companyId, doc_type: docType, status: filter !== "all" ? filter : undefined, limit: 100 })
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [companyId, docType, filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.invoices.filter(inv =>
    !search || inv.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    inv.number?.toLowerCase().includes(search.toLowerCase())
  );

  async function changeStatus(inv, status) {
    // Le passage à "paid" ouvre une modale pour saisir la vraie date de
    // règlement — les autres statuts changent immédiatement.
    if (status === "paid") {
      setPayDateModal({ invoice: inv, date: new Date().toISOString().slice(0, 10) });
      return;
    }
    try {
      const r = await api.invoices.status(inv.id, status);
      if (r?.warning) showToast(r.warning, "warning", 12000);
      load();
    } catch (err) { showToast(err.message, "error"); }
  }

  // « Déjà encaissée — à rapprocher » (02/11/2026) : remplace « Marquer payée » sur
  // les factures ; aucune écriture, levé au rapprochement du relevé
  async function markSettled(inv, marked) {
    if (marked && !window.confirm(`Marquer ${inv.number} « Déjà encaissée — à rapprocher » ?\n\nAucune écriture n'est passée : la facture reste due par le client en comptabilité tant que l'encaissement n'est pas relié au relevé (Comptes bancaires > « 🔎 Proposer les factures »).`)) return;
    try {
      const r = await api.invoices.settledMark([inv.id], marked);
      if (r.refused?.length) showToast(`${inv.number} : ${r.refused[0].reason}`, "warning");
      else showToast(marked ? "Facture marquée « Déjà encaissée — à rapprocher »." : "Marqueur retiré.", "success");
      load();
    } catch (err) { showToast(err.message, "error"); }
  }

  async function confirmMarkPaid() {
    if (!payDateModal) return;
    try {
      const r = await api.invoices.status(payDateModal.invoice.id, "paid", { payment_date: payDateModal.date });
      setPayDateModal(null);
      load();
      if (r?.warning) showToast(r.warning, "warning", 12000);
      else showToast("Facture marquée payée.", "success");
    } catch (err) { showToast(err.message, "error"); }
  }

  async function deleteInv(inv) {
    if (!window.confirm(`Supprimer ${inv.number} ?`)) return;
    try { await api.invoices.delete(inv.id); load(); showToast("Supprimé.", "success"); } catch (err) { showToast(err.message, "error"); }
  }

  async function convertToInvoice(inv) {
    if (!window.confirm(`Convertir "${inv.number}" en facture ?`)) return;
    try { await api.invoices.convert(inv.id); load(); showToast("Converti en facture.", "success"); } catch (err) { showToast(err.message, "error"); }
  }

  async function createAvoir(inv) {
    try { await api.invoices.avoir(inv.id); load(); showToast("Avoir créé.", "success"); } catch (err) { showToast(err.message, "error"); }
  }

  async function sendInvoice(inv) {
    if (!window.confirm(`Confirmer l'envoi de la facture ${inv.number} via facturation électronique ?\n\nCette action est irréversible.`)) return;
    try {
      await api.invoices.send(inv.id);
      load();
      showToast("Facture envoyée.", "success");
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  async function openPdf(inv) {
    try {
      // Facture importée de Dolibarr : on ouvre le PDF d'ORIGINE (le document émis)
      const blob = inv.import_source === "dolibarr"
        ? await api.salesOcrInvoices.fileByInvoice(inv.id)
        : await api.documents.invoicePdf(inv.id, "fr", docType);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Le blob reste valide le temps que l'onglet l'utilise ; nettoyage differe
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(err.message || "Impossible de charger le PDF.", "error");
    }
  }
  const docLabel = { facture: "facture", devis: "devis", avoir: "avoir" }[docType];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Rechercher un ${docLabel}…`}
          style={{ flex: 1, minWidth: 180, background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 12px", color: "#111827", fontSize: 13 }} />
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 12px", color: "#111827", fontSize: 13 }}>
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => setModal({ type: "create" })}
          style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 7, padding: "7px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
          + Nouveau {docLabel}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucun {docLabel} trouvé.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
                {["Numéro", "Client", "Date", "Échéance", "Montant HT", "TTC", "Statut", "Actions"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const st = STATUS_LABELS[inv.status] || STATUS_LABELS.draft;
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid #161b22" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#ffffff"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: "#38bdf8", cursor: "pointer" }}
                      onClick={() => setModal({ type: "edit", invoice: inv })}
                      title="Voir / modifier la facture">{inv.number}</td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>{inv.client_name}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "#4b5563" }}>{inv.date}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "#4b5563" }}>{inv.due_date || "—"}</td>
                    <td style={{ padding: "9px 12px", fontSize: 13 }}>{fmt(inv.ht_amount)} €</td>
                    <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600 }}>{fmt(inv.ttc_amount)} €</td>
                    <td style={{ padding: "9px 12px" }}>
                      <span style={{ background: st.color + "22", color: st.color, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button onClick={() => openPdf(inv)} style={actionBtn}>PDF</button>
                        <button onClick={() => setModal({ type: "edit", invoice: inv })} style={actionBtn} title="Modifier">✏️</button>
                        {docType === "devis" && (
                          <button onClick={() => convertToInvoice(inv)} title="Convertir en facture" style={{ ...actionBtn, color: "#16a34a" }}>→ FA</button>
                        )}
                        {docType === "facture" && inv.status === "paid" && (
                          <button onClick={() => createAvoir(inv)} title="Créer un avoir" style={{ ...actionBtn, color: "#f59e0b" }}>AV</button>
                        )}
                        {docType === "facture" && inv.status === "draft" && (
                          <button onClick={() => sendInvoice(inv)} title="Envoyer via facturation électronique" style={{ ...actionBtn, color: "#2563eb" }}>Envoyer</button>
                        )}
                        {inv.status !== "paid" && (
                          <select value={inv.status} onChange={e => changeStatus(inv, e.target.value)}
                            style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 5, padding: "3px 6px", color: "#111827", fontSize: 11, cursor: "pointer" }}>
                            {/* Facture : « Payée » retiré (02/11/2026) — l'encaissement se relie depuis le relevé */}
                            {Object.entries(STATUS_LABELS).filter(([k]) => !(docType === "facture" && k === "paid")).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                          </select>
                        )}
                        {docType === "facture" && ["pending", "late"].includes(inv.status) && !inv.settled_marked_at && (
                          <button onClick={() => markSettled(inv, true)} style={actionBtn}
                            title="Le client a payé, l'encaissement n'est pas encore relié au relevé : aucune écriture ; la facture sort de « À encaisser »">Déjà encaissée</button>
                        )}
                        {docType === "facture" && inv.settled_marked_at && inv.status !== "paid" && (
                          <>
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: "#6d28d9" }}>Encaissée — à rapprocher</span>
                            <button onClick={() => markSettled(inv, false)} style={actionBtn} title="Retirer le marqueur">Retirer le marqueur</button>
                          </>
                        )}
                        {inv.status === "draft" && (
                          <button onClick={() => deleteInv(inv)} style={{ ...actionBtn, color: "#ef4444" }} title="Supprimer">🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === "create" && (
        <InvoiceModal docType={docType} companyId={companyId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.type === "edit" && (
        <InvoiceModal invoice={modal.invoice} docType={docType} companyId={companyId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      {payDateModal && (
        <div style={{ position: "fixed", inset: 0, background: "#0007", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 24, width: 360 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Marquer payée — {payDateModal.invoice.number}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>Indiquez la date réelle de réception du paiement.</div>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Date de paiement</label>
            <input type="date" value={payDateModal.date}
              onChange={e => setPayDateModal(m => ({ ...m, date: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13, marginBottom: 16, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setPayDateModal(null)} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontSize: 13 }}>Annuler</button>
              <button onClick={confirmMarkPaid} style={{ padding: "7px 14px", borderRadius: 7, border: "none", background: "#16a34a", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BLList — delivery notes management tab
// ─────────────────────────────────────────────────────────────────
function BLList({ companyId }) {
  const { showToast } = useToast();
  const [data, setData]       = useState({ delivery_notes: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all");
  const [modal, setModal]     = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    api.deliveryNotes.list(companyId, { status: filter !== "all" ? filter : undefined })
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [companyId, filter]);

  useEffect(() => { load(); }, [load]);

  async function changeStatus(dn, status) {
    try { await api.deliveryNotes.status(dn.id, status); load(); } catch (err) { showToast(err.message, "error"); }
  }

  async function deleteDn(dn) {
    if (!window.confirm(`Supprimer ${dn.dn_number} ?`)) return;
    try { await api.deliveryNotes.delete(dn.id); load(); showToast("BL supprimé.", "success"); } catch (err) { showToast(err.message, "error"); }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 12px", color: "#111827", fontSize: 13 }}>
          <option value="all">Tous les statuts</option>
          {Object.entries(BL_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => setModal({ type: "create" })}
          style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 7, padding: "7px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}>
          + Nouveau BL
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div>
      ) : data.delivery_notes.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Aucun bon de livraison.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
              {["Numéro", "Client", "Date livraison", "Transporteur", "Statut", "Actions"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.delivery_notes.map(dn => {
              const st = BL_STATUS_LABELS[dn.status] || BL_STATUS_LABELS.brouillon;
              return (
                <tr key={dn.id} style={{ borderBottom: "1px solid #161b22" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#ffffff"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: "#38bdf8" }}>{dn.dn_number}</td>
                  <td style={{ padding: "9px 12px", fontSize: 13 }}>{dn.client_name}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "#4b5563" }}>{dn.delivery_date || "—"}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "#4b5563" }}>{dn.carrier || "—"}</td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{ background: st.color + "22", color: st.color, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={async () => {
                          try {
                            const { blob } = await api.deliveryNotes.pdfDownload(dn.id);
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank", "noopener,noreferrer");
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          } catch (err) {
                            showToast(err.message || "Impossible de charger le PDF.", "error");
                          }
                        }}
                        style={{ ...actionBtn, textDecoration: "none", display: "inline-block" }}>PDF</button>
                      <button onClick={() => setModal({ type: "edit", dn })} style={actionBtn}>✏️</button>
                      {dn.status !== "livré" && dn.status !== "annulé" && (
                        <select value={dn.status} onChange={e => changeStatus(dn, e.target.value)}
                          style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 5, padding: "3px 6px", color: "#111827", fontSize: 11, cursor: "pointer" }}>
                          {Object.entries(BL_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      )}
                      {dn.status === "brouillon" && (
                        <button onClick={() => deleteDn(dn)} style={{ ...actionBtn, color: "#ef4444" }}>🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal?.type === "create" && <BLModal companyId={companyId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "edit"   && <BLModal dn={modal.dn} companyId={companyId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ScheduleList — liste consolidée de toutes les échéances
// ─────────────────────────────────────────────────────────────────
const SCHED_STATUS = {
  en_attente: { label: "En attente", color: "#f59e0b" },
  payé:       { label: "Payée",      color: "#16a34a" },
  en_retard:  { label: "En retard",  color: "#ef4444" },
  annulé:     { label: "Annulée",    color: "#4b5563" },
};

function ScheduleList({ companyId }) {
  const { showToast } = useToast();
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    api.paymentSchedule.upcoming({ company_id: companyId, limit: 300 })
      .then(setRows).catch(console.error).finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Facture de l'échéance marquée « Déjà encaissée — à rapprocher » (aucune écriture)
  async function markSettledInvoice(row) {
    if (!window.confirm(`Marquer la facture ${row.invoice_number || ""} « Déjà encaissée — à rapprocher » ?\n\nAucune écriture n'est passée : l'encaissement se relie ensuite depuis le relevé (Comptes bancaires > « 🔎 Proposer les factures »).`)) return;
    try {
      const r = await api.invoices.settledMark([row.invoice_id], true);
      if (r.refused?.length) showToast(r.refused[0].reason, "warning");
      else showToast("Facture marquée « Déjà encaissée — à rapprocher ».", "success");
      load();
    } catch (err) {
      showToast(err.message, "error");
    }
  }

  function isLate(r) {
    return r.status === "en_attente" && r.due_date && r.due_date < today;
  }

  const filtered = rows.filter(r => {
    if (statusFilter === "all")        return true;
    if (statusFilter === "en_retard")  return isLate(r);
    if (statusFilter === "en_attente") return r.status === "en_attente" && !isLate(r);
    return r.status === statusFilter;
  });

  const monthlyTotals = {};
  filtered.forEach(r => {
    if (r.due_date && r.status !== "payé" && r.status !== "annulé") {
      const m = r.due_date.slice(0, 7);
      monthlyTotals[m] = (monthlyTotals[m] || 0) + (r.amount || 0);
    }
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 12px", color: "#111827", fontSize: 13 }}>
          <option value="all">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="en_retard">En retard</option>
          <option value="payé">Payées</option>
          <option value="annulé">Annulées</option>
        </select>
        <button onClick={load}
          style={{ background: "#f9fafb", color: "#4b5563", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
          🔄 Actualiser
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
          <div>Aucune échéance trouvée. Créez une facture avec paiement échelonné.</div>
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto", marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
                  {["Facture", "Client", "Libellé", "Montant TTC", "Date", "Statut", "Actions"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, color: "#4b5563", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const late = isLate(row);
                  const st   = late ? SCHED_STATUS.en_retard : SCHED_STATUS[row.status] || SCHED_STATUS.en_attente;
                  return (
                    <tr key={row.id}
                      style={{ borderBottom: "1px solid #161b22", background: late ? "rgba(239,68,68,.04)" : "" }}
                      onMouseEnter={e => e.currentTarget.style.background = late ? "rgba(239,68,68,.08)" : "#ffffff"}
                      onMouseLeave={e => e.currentTarget.style.background = late ? "rgba(239,68,68,.04)" : ""}>
                      <td style={{ padding: "9px 12px", fontSize: 13, color: "#38bdf8", fontWeight: 600 }}>{row.invoice_number}</td>
                      <td style={{ padding: "9px 12px", fontSize: 13 }}>{row.client_name}</td>
                      <td style={{ padding: "9px 12px", fontSize: 12, color: "#4b5563" }}>
                        {row.label || `Échéance ${row.installment_number}`}
                      </td>
                      <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600 }}>{fmt(row.amount)} €</td>
                      <td style={{ padding: "9px 12px", fontSize: 12 }}>
                        <span style={{ color: late ? "#ef4444" : "#4b5563" }}>{row.due_date || "—"}</span>
                        {late && (
                          <span style={{ marginLeft: 6, fontSize: 10, background: "#ef444422", color: "#ef4444", borderRadius: 4, padding: "1px 5px" }}>
                            EN RETARD
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "9px 12px" }}>
                        <span style={{ background: st.color + "22", color: st.color, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: "9px 12px" }}>
                        {/* « Marquer payée » retiré (02/11/2026) : le règlement se relie depuis le relevé ;
                            facture déjà payée par le client → « Déjà encaissée » (aucune écriture) */}
                        {row.status !== "payé" && row.status !== "annulé" && (
                          <button onClick={() => markSettledInvoice(row)} title="Le client a payé, l'encaissement n'est pas encore relié au relevé : aucune écriture"
                            style={{ fontSize: 11, padding: "4px 10px", borderRadius: 5, background: "#6d28d914", border: "1px solid #6d28d9", color: "#6d28d9", cursor: "pointer" }}>
                            Déjà encaissée
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {Object.keys(monthlyTotals).length > 0 && (
            <div style={{ background: "#ffffff", borderRadius: 8, padding: 16 }}>
              <div style={{ fontWeight: 600, color: "#111827", fontSize: 13, marginBottom: 10 }}>
                Totaux mensuels à encaisser
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Object.entries(monthlyTotals).sort(([a], [b]) => a.localeCompare(b)).map(([month, total]) => (
                  <div key={month} style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 18px" }}>
                    <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 4 }}>
                      {new Date(month + "-01").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#38bdf8" }}>{fmt(total)} €</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
export default function Facturation({ currentCompanyId, onNavigate }) {
  const [activeTab, setActiveTab] = useState("factures");
  const [showImport, setShowImport] = useState(false);
  const [showOpening, setShowOpening] = useState(false);
  const [showDeposits, setShowDeposits] = useState(false);
  const [listRefresh, setListRefresh] = useState(0);
  useEffect(() => {
    let intent = null;
    try { intent = sessionStorage.getItem("execpro360.intent"); if (intent) sessionStorage.removeItem("execpro360.intent"); } catch {}
    if (intent === "opening") setShowOpening(true);
    else if (intent === "salesImport") { setActiveTab("ocr"); setShowImport(true); }
  }, []);
  const [ocrRefresh, setOcrRefresh] = useState(0);

  const tabContent = () => {
    switch (activeTab) {
      case "ocr":       return <><SalesInvoiceOcrTab key={ocrRefresh} companyId={currentCompanyId} onImport={() => setShowImport(true)} onOpenDeposits={() => setShowDeposits(true)} /><SalesInflowsToValidate onNavigate={onNavigate} /></>;
      case "encaisser": return <SalesReceivables onNavigate={onNavigate} />;
      case "factures":  return <InvoiceList key={listRefresh} docType="facture" companyId={currentCompanyId} onNavigate={onNavigate} />;
      case "devis":     return <InvoiceList docType="devis"   companyId={currentCompanyId} onNavigate={onNavigate} />;
      case "avoirs":    return <InvoiceList docType="avoir"   companyId={currentCompanyId} onNavigate={onNavigate} />;
      case "bl":        return <BLList companyId={currentCompanyId} />;
      case "commandes": return (
        <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 16, color: "#4b5563", marginBottom: 20 }}>Les commandes clients sont gérées dans le module dédié.</div>
          <button onClick={() => onNavigate?.("customerOrders")}
            style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
            Aller aux commandes clients →
          </button>
        </div>
      );
      case "relances": return (
        <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📬</div>
          <div style={{ fontSize: 16, color: "#4b5563", marginBottom: 20 }}>Les relances automatiques sont gérées dans le module dédié.</div>
          <button onClick={() => onNavigate?.("relances")}
            style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
            Aller aux relances →
          </button>
        </div>
      );
      case "echeancier": return <ScheduleList companyId={currentCompanyId} />;
      default: return null;
    }
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1300, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Facturation</h1>
        {/* Bouton visible (lot B) : import des factures de vente Dolibarr */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={() => setShowOpening(true)} title="Factures de l'exercice antérieur encore impayées">📂 Créances d'ouverture</button>
          <button className="btn" onClick={() => setShowDeposits(true)} title="Acomptes reçus avant le début de l'exercice, pas encore déduits">📂 Acomptes d'ouverture</button>
          <button className="btn primary" onClick={() => setShowImport(true)}>📥 Importer des factures de vente</button>
        </div>
      </div>
      {showImport && (
        <SalesImportModal companyId={currentCompanyId}
          onClose={() => setShowImport(false)}
          onDone={() => { setActiveTab("ocr"); setOcrRefresh(n => n + 1); }} />
      )}
      {showDeposits && <OpeningDepositsModal companyId={currentCompanyId} onClose={() => { setShowDeposits(false); setOcrRefresh(n => n + 1); }} />}
      {showOpening && (
        <OpeningReceivablesModal companyId={currentCompanyId}
          onClose={() => setShowOpening(false)}
          onDone={() => { setActiveTab("factures"); setListRefresh(n => n + 1); }} />
      )}

      <SalesCorrections companyId={currentCompanyId} onChanged={() => setListRefresh(n => n + 1)} />

      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              background: "none", border: "none",
              borderBottom: activeTab === t.id ? "2px solid #3b82f6" : "2px solid transparent",
              color: activeTab === t.id ? "#111827" : "#4b5563",
              padding: "10px 18px", cursor: "pointer", fontSize: 13,
              fontWeight: activeTab === t.id ? 600 : 400,
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "24px 28px" }}>
        {tabContent()}
      </div>
    </div>
  );
}
